/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The geofence list's filters and sort. The filters live in the query string (`q`, `category`,
 * `visibility`, `layer`) so a link — the categories page's geofence counts, say — opens the
 * list already filtered, and opening a geofence keeps them.
 */

import { type GeofenceFilters, type ManagedGeofence, NOT_CATEGORISED, type SortState } from './types'

export const EMPTY_FILTERS: GeofenceFilters = { search: '', categories: [], visibility: 'all', layers: [] }

const VISIBILITIES: GeofenceFilters['visibility'][] = ['all', 'visible', 'hidden']

const list = (value: string | null) => (value ? value.split(',').filter(Boolean) : [])

export const readFilters = (params: URLSearchParams): GeofenceFilters => {
  const visibility = params.get('visibility') as GeofenceFilters['visibility'] | null
  return {
    search: params.get('q') ?? '',
    categories: list(params.get('category')),
    visibility: visibility && VISIBILITIES.includes(visibility) ? visibility : 'all',
    layers: list(params.get('layer'))
      .map(Number)
      .filter((layer) => Number.isInteger(layer) && layer >= 1 && layer <= 5),
  }
}

/** The filters written into `params`, leaving its other keys (the company) alone. */
export const writeFilters = (params: URLSearchParams, filters: GeofenceFilters): URLSearchParams => {
  const next = new URLSearchParams(params)
  const set = (key: string, value: string) => (value ? next.set(key, value) : next.delete(key))
  set('q', filters.search)
  set('category', filters.categories.join(','))
  set('visibility', filters.visibility === 'all' ? '' : filters.visibility)
  set('layer', filters.layers.join(','))
  return next
}

export const activeFilterCount = (filters: GeofenceFilters): number =>
  (filters.search.trim() ? 1 : 0) +
  (filters.categories.length ? 1 : 0) +
  (filters.visibility === 'all' ? 0 : 1) +
  (filters.layers.length ? 1 : 0)

export const applyFilters = (geofences: ManagedGeofence[], filters: GeofenceFilters): ManagedGeofence[] => {
  const search = filters.search.trim().toLowerCase()
  const categoryIds = new Set(filters.categories.filter((c) => c !== NOT_CATEGORISED).map(Number))
  const wantsUncategorised = filters.categories.includes(NOT_CATEGORISED)
  const layers = new Set(filters.layers)

  return geofences.filter((geofence) => {
    if (search && !geofence.name.toLowerCase().includes(search)) return false
    if (filters.visibility === 'visible' && !geofence.visible) return false
    if (filters.visibility === 'hidden' && geofence.visible) return false
    if (layers.size && !layers.has(geofence.zorder)) return false
    if (filters.categories.length) {
      const inCategory = geofence.categoryIds.some((id) => categoryIds.has(id))
      const uncategorised = geofence.categoryIds.length === 0
      if (!inCategory && !(wantsUncategorised && uncategorised)) return false
    }
    return true
  })
}

export const sortGeofences = (geofences: ManagedGeofence[], sort: SortState): ManagedGeofence[] => {
  const byName = (a: ManagedGeofence, b: ManagedGeofence) =>
    a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
  const compare = (a: ManagedGeofence, b: ManagedGeofence) => {
    if (sort.key === 'zorder') return a.zorder - b.zorder || byName(a, b)
    if (sort.key === 'visible') return Number(b.visible) - Number(a.visible) || byName(a, b)
    return byName(a, b)
  }
  const sorted = [...geofences].sort(compare)
  return sort.ascending ? sorted : sorted.reverse()
}

/** How many geofences use each category, for the categories table. */
export const countByCategory = (geofences: ManagedGeofence[]): Map<number, number> => {
  const counts = new Map<number, number>()
  for (const geofence of geofences) {
    for (const id of geofence.categoryIds) counts.set(id, (counts.get(id) ?? 0) + 1)
  }
  return counts
}
